import type { Meta, SectionName } from './types';
import { formatCurrency, MarketSettings } from 'src/components/currency';
import type { HrefLang, Product } from 'src/types';
import type { ClientMarket } from '@prisjakt/web-app-config/types/client-market';
import type {
  MarketCode,
  MarketConfig,
} from '@prisjakt/web-app-config/dist/markets/types';
import type { LinkToFunc } from 'src/components/app-context';
import { sectionClassicParam, sectionMap } from './constants';
import { getFiltersConfig } from 'src/components/price-list/filters-config';
import { sortOffersByPrice } from 'src/components/price-list/sort-by-price';
import { filterOffers } from 'src/components/price-list/filtering';
import { FilterType, PriceDisplayEnum } from 'src/components/price-list/types';

/**
 * Hackish way of emulating the default client side filtering of offers on product page.
 */
function getLowestPrice(product: Product) {
  const defaultFilters = getFiltersConfig().map(filter => ({
    ...filter,
    filterAlternativeOffers: filter.type !== FilterType.CONDITION,
  }));
  const filteredOffers = filterOffers(defaultFilters, product.prices);
  const firstSort = sortOffersByPrice(PriceDisplayEnum.PRICE, filteredOffers);
  return (
    firstSort?.[0]?.price?.exclShipping || firstSort?.[0]?.price?.inclShipping
  );
}

export function getMeta(product: Product, market: MarketCode): Meta {
  return {
    name: product.name,
    numberOfShops: product.prices?.meta?.itemsTotal || 0,
    numberOfOffers: product.prices?.nodes?.length || 0,
    lowestPrice: formatCurrency(getLowestPrice(product), market, false) || '',
    brand: product.brand?.name,
    category: product.category?.name || '',
  };
}

export function getTitleTranslationKey(
  section: SectionName,
  product: Product,
  market: MarketCode
) {
  switch (section) {
    case 'properties':
      return 'product-properties.title';
    case 'reviews':
      return 'product-reviews.title';
    case 'statistics':
      return 'product-price-history.title';
    default: {
      // @NOTE: Product meta data experiment (SE, GB, NZ only)
      // Return different meta data based on product ID
      if (!['se', 'gb', 'nz'].includes(market)) {
        return 'product.title';
      }
      switch (product.id % 10) {
        case 2:
          return 'product.title-alt-1';
        case 4:
          return 'product.title-alt-2';
        case 6:
          return 'product.title-alt-3';
        case 8:
          return 'product.title-alt-4';
        default:
          return 'product.title';
      }
    }
  }
}

export function getRoundedCurrency(price: number, { code }: { code: string }) {
  return parseFloat(price.toFixed(MarketSettings[code]?.numberOfDecimals));
}

function getMainDescriptionTranslationKey(
  product: Product,
  market: MarketCode
) {
  if (
    !product.prices ||
    !product.prices.meta ||
    !product.prices.meta.itemsTotal
  ) {
    return 'product-no-shops.description';
  }

  if (!product.brand) {
    return 'product-no-brand.description';
  }

  // @NOTE: Product meta data experiment (SE, GB, NZ only)
  // Return different meta data based on product ID
  if (['se', 'gb', 'nz'].includes(market)) {
    switch (product.id % 10) {
      case 1:
        return 'product.description-alt-1';
      case 3:
        return 'product.description-alt-2';
      case 5:
        return 'product.description-alt-3';
      case 7:
        return 'product.description-alt-4';
      default:
        return 'product.description';
    }
  }

  return 'product.description';
}

export function getDescriptionTranslationKey(
  section: SectionName,
  product: Product,
  market: MarketCode
) {
  switch (section) {
    case 'properties':
      return 'product-properties.description';
    case 'reviews':
      return 'product-reviews.description';
    case 'statistics':
      return 'product-price-history.description';
    default:
      return getMainDescriptionTranslationKey(product, market);
  }
}

export function formatUrl(
  market: ClientMarket | MarketConfig | undefined,
  section: SectionName,
  url: string,
  id: string | number
) {
  if (!market) {
    return url;
  }

  if (
    !section ||
    section === 'main' ||
    (market && market.useHashForProductTabs)
  ) {
    return url;
  }

  if (/\?p=/.test(url)) {
    return url.replace(`?p=${id}`, `?${sectionClassicParam[section]}=${id}`);
  }

  if (/--p/.test(url)) {
    return `${url}/${section}`;
  }

  return url;
}

export function getLinkType(section: SectionName) {
  return sectionMap[section || 'main'];
}

/**
 *
 * @param val Expects a value between 0 and 10, which will then be normalized to 0 and 5.
 * @returns number
 */
export function getRatingValue(val: number | null | undefined) {
  return val !== undefined && val !== null
    ? parseFloat(
        (val / 2).toFixed(1) // Convert rating
      )
    : 0;
}

export function getCanonical(
  product: Product,
  section: SectionName,
  market: ClientMarket,
  linkTo: LinkToFunc
) {
  return (
    market.domain +
    (market.useHashForProductTabs
      ? linkTo('product', product)
      : linkTo(getLinkType(section), product))
  );
}

export function getHrefLang(
  product: Product,
  markets: Record<MarketCode, MarketConfig>,
  section: SectionName
) {
  return product.hrefLang
    ? product.hrefLang
        .filter(h => h !== undefined && h !== null && h.locale && h.url)
        .map(item => {
          const { locale, url } = item as HrefLang;
          return {
            locale,
            url: formatUrl(
              Object.values(markets).find(
                m => m.hrefLang && m.hrefLang.indexOf(locale) !== -1
              ),
              section,
              url,
              product.id
            ),
          };
        })
    : [];
}
